import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, FileEdit, TrendingUp, AlertTriangle, Loader2, Lock, Search } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { formatINR, formatInt } from "../lib/format";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

function monthDefault() {
  const t = new Date();
  const first = new Date(t.getFullYear(), t.getMonth(), 1);
  const last = new Date(t.getFullYear(), t.getMonth() + 1, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(last) };
}

const todayIso = () => new Date().toISOString().slice(0, 10);
const yesterdayIso = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };

function SummaryCard({ testid, icon: Icon, label, count, amount, tone, sub, onClick }) {
  const tones = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
    red: "border-red-200 bg-red-50 text-red-700",
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-xl border p-4 text-left transition-all hover:shadow-md ${tones[tone]} ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="font-display text-2xl font-bold tabular-nums">{formatInt(count)}</div>
      {amount !== undefined && (
        <div className="mt-0.5 text-sm font-semibold tabular-nums">{amount === null ? <span className="inline-flex items-center gap-1 text-slate-400"><Lock className="h-3 w-3" /> hidden</span> : formatINR(amount)}</div>
      )}
      {sub && <div className="mt-1 text-[11px] leading-tight opacity-80">{sub}</div>}
    </button>
  );
}

export default function CommercialWorkSummary() {
  const { ctx, drillDown } = useWorkspace();
  const pid = ctx.project?.id;
  const [range, setRange] = useState(monthDefault());
  const [pickMode, setPickMode] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unresolvedOpen, setUnresolvedOpen] = useState(false);
  const [unresolved, setUnresolved] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    if (!range.from || !range.to) return;
    setLoading(true);
    api.get("/dashboard/commercial-summary", {
      params: { ...(pid ? { project_id: pid } : {}), install_from: range.from, install_to: range.to },
    }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [pid, range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const canComm = data?.can_view_commercials;
  const showProjectCol = data?.project_scope === "all";
  const _q = search.trim().toLowerCase();
  const filteredRows = (data?.by_final_remark || []).filter((r) => !_q
    || (r.final_remark_name || "").toLowerCase().includes(_q)
    || (r.project_name || "").toLowerCase().includes(_q));

  const drill = (extra) => drillDown("data-entry-register", { install_from: range.from, install_to: range.to, ...extra });

  const openUnresolved = () => {
    setUnresolvedOpen(true);
    setUnresolved(null);
    api.get("/dashboard/commercial-summary/unresolved-drafts", {
      params: { ...(pid ? { project_id: pid } : {}), install_from: range.from, install_to: range.to },
    }).then((r) => setUnresolved(r.data)).catch(() => setUnresolved({ items: [], total: 0 }));
  };

  return (
    <div data-testid="commercial-work-summary">
      <div className="mb-3 mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-lg font-semibold text-slate-900">Commercial Work Summary</h2>
          <Badge variant="outline" className="border-slate-300 text-xs font-medium text-slate-500" data-testid="cws-date-basis">Date Basis: {data?.date_basis || "Installation Date"}</Badge>
        </div>
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <div className="flex items-center gap-1 pb-0.5">
            <Button data-testid="cws-today" size="sm" variant="outline" className="h-8" onClick={() => { const t = todayIso(); setRange({ from: t, to: t }); }}>Today</Button>
            <Button data-testid="cws-yesterday" size="sm" variant="outline" className="h-8" onClick={() => { const y = yesterdayIso(); setRange({ from: y, to: y }); }}>Yesterday</Button>
            <Button data-testid="cws-pick-toggle" size="sm" variant={pickMode ? "default" : "outline"} className={`h-8 ${pickMode ? "bg-teal-700 hover:bg-teal-800" : ""}`} onClick={() => setPickMode((s) => !s)}>Pick Date</Button>
          </div>
          {pickMode && (
            <div><Label className="text-xs text-slate-500">Date</Label><Input data-testid="cws-day" type="date" value={range.from === range.to ? range.from : todayIso()} onChange={(e) => setRange({ from: e.target.value, to: e.target.value })} className="mt-1 h-8 w-40" /></div>
          )}
          <div><Label className="text-xs text-slate-500">From</Label><Input data-testid="cws-from" type="date" value={range.from} max={range.to} onChange={(e) => setRange((s) => ({ ...s, from: e.target.value }))} className="mt-1 h-8 w-40" /></div>
          <div><Label className="text-xs text-slate-500">To</Label><Input data-testid="cws-to" type="date" value={range.to} min={range.from} onChange={(e) => setRange((s) => ({ ...s, to: e.target.value }))} className="mt-1 h-8 w-40" /></div>
          <Button data-testid="cws-clear" size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-slate-800" title="Reset to this month" onClick={() => { setRange(monthDefault()); setPickMode(false); }}>Clear</Button>
        </div>
      </div>

      {!canComm && data && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="cws-no-commercial-note">
          <Lock className="h-3.5 w-3.5" /> You can view operational counts, but commercial amounts are restricted for your role.
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center rounded-xl border border-slate-200 bg-white py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : !data ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No commercial data for this range</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <SummaryCard testid="cws-card-completed" icon={CheckCircle2} tone="green" label="Completed" count={data.completed.count} amount={data.completed.amount} sub="Frozen commercial value" onClick={() => drill({ status: "COMPLETED", label: "Completed entries" })} />
            <SummaryCard testid="cws-card-draft" icon={FileEdit} tone="amber" label="Draft · Provisional" count={data.draft.count} amount={data.draft.resolved_amount} sub={`${formatInt(data.draft.resolved_count)} resolved · provisional est.`} onClick={() => drill({ status: "DRAFT", label: "Draft entries" })} />
            <SummaryCard testid="cws-card-total" icon={TrendingUp} tone="indigo" label="Total Potential" count={data.total.count} amount={data.total.potential_amount} sub="Frozen + provisional (draft est.)" onClick={() => drill({ label: "All entries" })} />
            <SummaryCard testid="cws-card-unresolved" icon={AlertTriangle} tone="red" label="Unresolved Draft" count={data.draft.unresolved_count} sub="No commercial value — click to audit" onClick={openUnresolved} />
          </div>
          {data.reconcile && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="cws-reconcile">
              <span className="font-semibold text-slate-700">Data Entry Register (all dates): </span>
              {formatInt(data.reconcile.register_completed)} Completed, {formatInt(data.reconcile.register_drafts)} Draft.
              {" "}This summary counts only entries with Installation Date in the selected range.
              {data.reconcile.completed_outside_range > 0 && <span className="ml-1" data-testid="cws-reconcile-outside">· {formatInt(data.reconcile.completed_outside_range)} completed outside range</span>}
              {data.reconcile.completed_no_commercial_value > 0 && <span className="ml-1 text-red-600" data-testid="cws-reconcile-novalue">· {formatInt(data.reconcile.completed_no_commercial_value)} completed without commercial value</span>}
              {data.reconcile.drafts_outside_range > 0 && <span className="ml-1" data-testid="cws-reconcile-drafts-outside">· {formatInt(data.reconcile.drafts_outside_range)} drafts outside range</span>}
            </div>
          )}

          {/* Final Remark-wise breakdown — boxed, searchable, vertical scroller */}
          <div className="mt-5 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="cws-breakdown-box">
            <div className="flex flex-col gap-2 border-b border-slate-100 p-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Final Remark-wise Breakdown <span className="text-xs font-normal text-slate-400">({filteredRows.length})</span></h3>
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="cws-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search final remark / project..." className="h-9 pl-9" />
              </div>
            </div>

            {/* Desktop table */}
            <div className="hidden max-h-[420px] overflow-auto md:block" data-testid="cws-breakdown-desktop">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                    {showProjectCol && <th className="sticky top-0 bg-slate-100 px-3 py-2.5 font-semibold">Project</th>}
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 font-semibold">Final Remark</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">Completed</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">Completed ₹</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">Draft</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">Draft Provisional ₹</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">Unresolved</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">Total</th>
                    <th className="sticky top-0 bg-slate-100 px-3 py-2.5 text-right font-semibold">Potential ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr><td colSpan={showProjectCol ? 9 : 8} className="px-3 py-8 text-center text-slate-400">{_q ? "No match" : "No entries in this range"}</td></tr>
                  ) : filteredRows.map((r) => (
                    <tr key={`${r.project_id}-${r.final_remark_id || "none"}`} data-testid={`cws-row-${r.final_remark_id || "none"}`} className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50" onClick={() => drill({ final_remark_id: r.final_remark_id || "__none__", label: r.final_remark_name })}>
                      {showProjectCol && <td className="px-3 py-2.5 text-slate-600">{r.project_name}</td>}
                      <td className="px-3 py-2.5 font-medium text-slate-800">{r.final_remark_name}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatInt(r.completed_count)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{formatINR(r.completed_amount)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatInt(r.draft_count)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{formatINR(r.draft_resolved_amount)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.draft_unresolved_count > 0 ? <span className="font-semibold text-red-600">{formatInt(r.draft_unresolved_count)}</span> : "0"}</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatInt(r.total_count)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-indigo-700">{formatINR(r.potential_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="max-h-[420px] space-y-3 overflow-auto p-3 md:hidden" data-testid="cws-breakdown-mobile">
              {filteredRows.length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-400">{_q ? "No match" : "No entries in this range"}</div>
              ) : filteredRows.map((r) => (
                <div key={`${r.project_id}-${r.final_remark_id || "none"}`} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" onClick={() => drill({ final_remark_id: r.final_remark_id || "__none__", label: r.final_remark_name })}>
                  <div className="mb-1 font-medium text-slate-800">{r.final_remark_name}</div>
                  {showProjectCol && <div className="mb-2 text-xs text-slate-500">{r.project_name}</div>}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-slate-400">Completed</span><div className="font-semibold">{formatInt(r.completed_count)} · <span className="text-emerald-700">{formatINR(r.completed_amount)}</span></div></div>
                    <div><span className="text-slate-400">Draft (prov.)</span><div className="font-semibold">{formatInt(r.draft_count)} · <span className="text-amber-700">{formatINR(r.draft_resolved_amount)}</span></div></div>
                    <div><span className="text-slate-400">Unresolved</span><div className={r.draft_unresolved_count > 0 ? "font-semibold text-red-600" : "font-semibold"}>{formatInt(r.draft_unresolved_count)}</div></div>
                    <div><span className="text-slate-400">Potential</span><div className="font-semibold text-indigo-700">{formatINR(r.potential_amount)}</div></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Dialog open={unresolvedOpen} onOpenChange={setUnresolvedOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="cws-unresolved-dialog">
          <DialogHeader><DialogTitle>Unresolved Draft Entries</DialogTitle></DialogHeader>
          {!unresolved ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : unresolved.items.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No unresolved drafts in this range</div>
          ) : (
            <div className="space-y-2 text-sm">
              <p className="text-xs text-slate-500">These drafts are counted but excluded from provisional value because their commercial value could not be resolved.</p>
              {unresolved.items.map((u) => (
                <div key={u.id} className="rounded-md border border-red-100 bg-red-50/50 p-3" data-testid={`cws-unresolved-${u.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-semibold text-slate-800">{u.entry_number}</span>
                    <span className="text-xs text-slate-500">{u.installation_date || "no install date"}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-600">{u.project_name}{u.final_remark_name ? ` · ${u.final_remark_name}` : ""}</div>
                  <div className="mt-1 text-xs font-medium text-red-600">{u.reason}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
